import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, TextField, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';

export default function JDIntakePopover({
  open,
  onClose,
  onConfirm,
  initialValue = ''
}) {
  const [value, setValue] = useState(initialValue || '');

  useEffect(() => {
    if (open) setValue(initialValue || '');
  }, [open, initialValue]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        try { if (typeof onClose === 'function') onClose(); } catch (_) {}
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') {
      onConfirm((value || '').trim());
    }
  };

  if (!open) return null;

  const isDisabled = !value || !value.trim();

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
      }}
    >
      <Box
        sx={{
          width: 'min(680px, 96vw)',
          maxHeight: '85vh',
          bgcolor: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 18px 56px rgba(0,0,0,0.36)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #EAECF0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <DragIndicatorIcon sx={{ color: '#98A2B3' }} fontSize="small" />
            <Typography
              variant="subtitle1"
              sx={{ fontSize: 18, fontWeight: 600, color: '#101828' }}
            >
              Job Description
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={() => { try { if (typeof onClose === 'function') onClose(); } catch (_) {} }}
            size="small"
            sx={{ color: '#667085' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: 16, fontWeight: 600, color: '#101828', mb: 0.5 }}
          >
            Start your intake
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#667085', mb: 2 }}
          >
            Base your search on an existing job description or job description link.
          </Typography>

          <TextField
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter a job description URL or copy and paste the relevant job description text here"
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: '10px',
                bgcolor: '#FFFFFF',
                color: '#101828', // ensure input text renders dark
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#CFE1FF' // subtle blue border
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9EC2FF'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3B82F6',
                  borderWidth: '1px'
                },
                // Ensure textarea/input text is black and placeholder is readable
                '& .MuiInputBase-input': {
                  color: '#101828'
                },
                // Fix height and make the inner textarea scrollable
                '& .MuiInputBase-inputMultiline': {
                  color: '#101828',
                  overflowY: 'auto',
                  maxHeight: '280px'
                },
                '& textarea': {
                  color: '#101828'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#98A2B3',
                  opacity: 1
                },
                '& textarea::placeholder': {
                  color: '#98A2B3',
                  opacity: 1
                }
              }
            }}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: '1px solid #EAECF0'
          }}
        >
          <Button
            onClick={() => { try { if (typeof onClose === 'function') onClose(); } catch (_) {} }}
            size="medium"
            variant="text"
            sx={{
              color: '#101828',
              borderRadius: '9999px',
              px: 1.5,
              py: 1,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(16,24,40,0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            size="medium"
            variant="contained"
            sx={{
              bgcolor: '#4599FA',
              color: '#fff',
              borderRadius: '9999px',
              px: 2.5,
              py: 1,
              minWidth: '160px',
              textTransform: 'none',
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
              '&:hover': { bgcolor: '#3b82d6' }
            }}
          >
            Use Job Description
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
